import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  Button,
  IconButton,
} from "@mui/material";
import {
  HealthAndSafety,
  Watch,
  FitnessCenter,
  LocalDining,
  Pool,
  SelfImprovement,
  Biotech,
  Looks,
  MonitorHeart,
  AttachMoney,
  Mood,
  Visibility,
  Hearing,
  Restaurant,
  Warning,
  DirectionsWalk,
  EmojiEvents,
  LocalHospital,
  Diversity3,
  MedicalInformation,
  HealthAndSafetyOutlined,
  SafetyCheck,
} from "@mui/icons-material";

interface Benefit {
  id: number;
  name: string;
  category: string;
  description: string;
  status: "recommended" | "assigned" | "completed";
  start_date: string;
  end_date?: string;
  benefit_type: string;
}

interface MemberBenefitsTableProps {
  benefits: Benefit[];
  pastBenefits: boolean;
  onRecommend?: (benefitId: number) => void;
  onAssign?: (benefitId: number) => void;
  onComplete?: (benefitId: number) => void;
}

const getBenefitIcon = (benefitType: string) => {
  switch (benefitType.toLowerCase()) {
    // New categories added at the top
    case "sdoh":
      return <Diversity3 />;
    case "health":
      return <MedicalInformation />;
    case "dvh":
      return <HealthAndSafetyOutlined />;
    case "safety":
      return <SafetyCheck />;
    // Existing categories
    case "insurance":
      return <HealthAndSafety />;
    case "wearable":
      return <Watch />;
    case "lesson":
    case "gym":
      return <FitnessCenter />;
    case "diet":
      return <LocalDining />;
    case "analysis":
      return <Biotech />;
    case "styling":
      return <Looks />;
    case "monitoring":
      return <MonitorHeart />;
    case "flex":
      return <AttachMoney />;
    case "dental":
      return <Mood />;
    case "vision":
      return <Visibility />;
    case "hearing":
      return <Hearing />;
    case "meal":
      return <Restaurant />;
    case "emergency":
      return <Warning />;
    case "foot care":
      return <DirectionsWalk />;
    case "reward program":
      return <EmojiEvents />;
    case "medicaid":
      return <LocalHospital />;
    default:
      return <HealthAndSafety />;
  }
};

const MemberBenefitsTable: React.FC<MemberBenefitsTableProps> = ({
  benefits,
  pastBenefits,
  onAssign,
  onComplete,
}) => {
  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow
            sx={{
              backgroundColor: "#F1EFFD",
            }}
          >
            <TableCell sx={{ fontWeight: "bold" }}>Name</TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>Category</TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>Description</TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>Start Date</TableCell>
            {pastBenefits && (
              <TableCell sx={{ fontWeight: "bold" }}>End Date</TableCell>
            )}
            {!pastBenefits && (
              <TableCell sx={{ fontWeight: "bold" }}>Actions</TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {benefits.map((benefit, index) => (
            <TableRow key={index}>
              <TableCell>
                <Typography sx={{ fontWeight: "bold" }}>
                  {benefit.name}
                </Typography>
              </TableCell>
              <TableCell>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <IconButton size="small" sx={{ color: "#5958e1" }}>
                    {getBenefitIcon(benefit.benefit_type)}
                  </IconButton>
                  <Typography>{benefit.category}</Typography>
                </Box>
              </TableCell>
              <TableCell>{benefit.description}</TableCell>
              <TableCell>
                {new Date(benefit.start_date).toLocaleString()}
              </TableCell>
              {pastBenefits && (
                <TableCell>
                  {new Date(benefit.end_date as any).toLocaleString()}
                </TableCell>
              )}
              {!pastBenefits && (
                <TableCell>
                  {benefit.status === "recommended" && onAssign && (
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => onAssign(benefit.id)}
                      sx={{ mr: 1 }}
                    >
                      Assign
                    </Button>
                  )}
                  {benefit.status === "assigned" && onComplete && (
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => onComplete(benefit.id)}
                      sx={{ mr: 1 }}
                    >
                      Complete
                    </Button>
                  )}
                  <Button variant="outlined" color="primary">
                    See Details
                  </Button>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default MemberBenefitsTable;
